import { ApolloError } from '@apollo/client';
import useInterval from '@use-it/interval';
import delay from 'delay';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { usecheckVendorPassClaimMutation, usegetAccessPointQuery, useunlockAccessPointMutation } from '../../apollo';
import {
  AlertModal,
  BottomDrawerModal,
  Button,
  Hero,
  Icon,
  LoadingSpinner,
  Progress,
  QRScanner, VendorPasscodeVerification
} from '../../components';
import { Footer } from '../../components/Footer';
import { Timetable } from '../../components/Timetable';
import { CHECK_ACCESS_INTERVAL_MS } from '../../config';
import {
  getErrorMessage,
} from '../../helpers';
import {
  getAvailabilitySchedule,
  getAvailabilityState,
  getLockdownEndDateString,
  isDateAllowedOnTimetable,
  renderParagraph
} from '../../utils';
import styles from './styles';
import { TimetableRuleState } from '@chirp/access-verification';
import { EntryConfig } from '../Entry';
import useCountDown from 'react-countdown-hook';
import { LockState } from '../../components/UnlockDrawer';
import { colors } from '../../assets/styles';

interface ModalState {
  isOpen: boolean;
  isCloseable: boolean;
  content: JSX.Element | string | null;
  title: string | null;
}

interface VendorAccessState {
  showQRScanner: boolean;
  accessExpired: boolean;
  modalState: ModalState;
}

const VendorAccess: React.FC = () => {
  const accessPointId = useParams().accessPointId as string;
  const [hasAttemptedUnlock, setHasAttemptedUnlock] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState<boolean>(false);

  const [state, setState] = useState<VendorAccessState>({
    showQRScanner: false,
    accessExpired: false,
    modalState: {
      isOpen: false,
      isCloseable: true,
      content: null,
      title: null,
    },
  });

  const { accessExpired, modalState } = state;

  const onError = (error: any, title = 'Unexpected Error', isCloseable = false) => {
    setState({
      ...state,
      modalState: {
        title,
        isCloseable,
        isOpen: true,
        content: getErrorMessage(error as ApolloError),
      },
    });
  };

  const { data: accessPointsData, loading: loadingAccessPoint } = usegetAccessPointQuery({
    skip: !accessPointId,
    variables: { accessPointId },
    onError: error => onError(error, 'Unable to access entry point'),
  });

  const [unlockAccessPointMutation] = useunlockAccessPointMutation();
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [lockState, setLockState] = useState(LockState.LOCKED);

  const accessPoint = accessPointsData?.accessPoint;
  const property = accessPoint?.property;
  const propertyFeatureFlags = property?.featureFlags || [];
  const propertyVirtualDirectory = accessPoint?.property?.propertyVirtualDirectory;

  const isVirtualDirectoryEnabled = (
    propertyFeatureFlags.includes('VIRTUAL_DIRECTORY') &&
    !!propertyVirtualDirectory?.published
  );

  let leasingOfficeDisplayName = 'Leasing Office';

  if (isVirtualDirectoryEnabled && propertyVirtualDirectory?.leasingOfficeDisplayName) {
    leasingOfficeDisplayName = propertyVirtualDirectory.leasingOfficeDisplayName;
  }

  const [checkVendorPassClaim, vendorPassClaimResult] = usecheckVendorPassClaimMutation({
    variables: {
      input: { accessPointId },
    },
  });

  useEffect(() => {
    if (property?.propertyId) {
      checkVendorPassClaim().catch(error => onError(error, 'Unable to verify vendor access'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property?.propertyId]);

  const { data: vendorPassClaimData, loading: loadingVendorPassClaim } = vendorPassClaimResult;

  const vendorUserRole = vendorPassClaimData?.vendorPassUserRole;
  const vendorSchedule = vendorUserRole?.schedule;
  const vendorPassActivatesAtFormatted = vendorUserRole?.activatesAt
    ? moment(vendorUserRole?.activatesAt).format('MMM D, h:mma')
    : null;
  const vendorPassExpiresAtFormatted = vendorUserRole?.expiresAt
    ? moment(vendorUserRole?.expiresAt).format('MMM D, h:mma')
    : null;

  useInterval(() => {
    setState({
      ...state,
      accessExpired: vendorUserRole?.expiresAt ?
        moment(new Date()).isSameOrAfter(vendorUserRole.expiresAt) : false,
    });
  }, CHECK_ACCESS_INTERVAL_MS);

  const [countdown, setCountdown] = useState(0);
  const [startCountdown, setStartCountdown] = useState(false);
  const [timeLeft, { start, reset }] = useCountDown(countdown, 100);

  useEffect(() => {
    reset();
    if (startCountdown) {
      start(countdown);
    }
  }, [startCountdown, countdown, reset, start]);

  const completionPercent = !startCountdown ? 0 : (countdown - timeLeft) / countdown * 105;


  const alertModal = modalState.isOpen ? (
    <AlertModal
      {...modalState}
      closeModal={() => {
        if (modalState.isCloseable) {
          setState({
            ...state,
            modalState: {
              ...modalState,
              isOpen: false,
            },
          });
        }
      }}
    />
  ) : null;

  if (!modalState.isCloseable) {
    return alertModal;
  }

  if (loadingAccessPoint || loadingVendorPassClaim || !accessPoint) {
    return <LoadingSpinner />;
  }

  if (!vendorUserRole || accessExpired) {
    return (
      <>
        <VendorPasscodeVerification
          isVirtualDirectoryEnabled={isVirtualDirectoryEnabled}
          leasingOfficeDisplayName={leasingOfficeDisplayName}
          onVerified={() => checkVendorPassClaim().catch(onError)}
        />
        <Footer />
      </>
    );
  }

  const onUnlockError = (error: any) => {
    // @TODO: Check for FORBIDDEN error code once it's provided
    if (getErrorMessage(error) === 'You do not have access to this entry point') {
      // Force vendor to re-verify passcode if it was regenerated
      checkVendorPassClaim().catch(onError);
    } else if (!hasAttemptedUnlock) {
      delay(8000).then(() => setHasAttemptedUnlock(true));
    } else {
      onError(error, 'Unexpected Error', true);
    }
  };

  if (property && vendorUserRole && !accessExpired) {
    const propertyName = property?.name;
    const { schedule: accessPointSchedule } = accessPoint.publicAccessPointRoles.VENDOR_PASS;
    const { timezone } = property;

    const accessTime = new Date();
    const apScheduleAllowed = isDateAllowedOnTimetable(accessTime, accessPointSchedule, timezone);
    const vendorScheduleAllowed = isDateAllowedOnTimetable(accessTime, vendorSchedule, timezone);
    const apAvailableState = getAvailabilityState(accessTime, accessPointSchedule, timezone);

    const closeDrawer = async (timeout: number) => {
      setCountdown(timeout);
      setStartCountdown(true);

      await delay(timeout);
      setCountdown(0);
      setStartCountdown(false);
      setLockState(LockState.LOCKED);
      setIsUnlocking(false);

      await delay(8000);
      setHasAttemptedUnlock(true);
    };

    let heroStatus: EntryConfig['heroStatus'] = 'none';
    let header = '';
    let subHeader: EntryConfig['subHeader'] = '';

    switch (apAvailableState) {
      case TimetableRuleState.CONTROLLED:
        if (vendorScheduleAllowed) {
          heroStatus = 'granted';
          header = 'Access granted!';
        } else {
          heroStatus = 'denied';
          header = 'Outside Access Schedule'
        }

        if (!apScheduleAllowed) {
          heroStatus = 'denied';
          header = 'This entry point is closed right now';
        }

        break;
      case TimetableRuleState.LOCKDOWN:
        heroStatus = 'denied';
        header = 'This entry point is closed right now';
        subHeader = renderParagraph(getLockdownEndDateString(new Date(), accessPointSchedule, timezone));

        break;
      case TimetableRuleState.UNLOCKED:
        heroStatus = 'unlocked';
        header = `Welcome to ${property.name}!`;
        subHeader = 'This entry point is unlocked right now.';

        break;
      default:
    }

    if (!hasAttemptedUnlock) {
      return (
        <>
          <div className={styles.container}>
            <div>
              <Hero
                title={accessPoint.name}
                subtitle={propertyName}
                cloudId={accessPoint.image?.cloudId}
                imageAlt={accessPoint.name}
                status={heroStatus}
              />
              <div className={styles.header}>
                {header}
              </div>
              {subHeader && (
                <div className={styles.subHeader}>
                  {
                    typeof subHeader === 'function'
                      ? subHeader(accessPoint.timetable, timezone)
                      : subHeader
                  }
                </div>
              )}
              {accessPoint.timetable &&
                getAvailabilitySchedule(new Date(), accessPoint.timetable, timezone).length > 0 && (
                  <div className={styles.scheduleWrapper}>
                    {`${accessPoint.name} Hours`}
                    {getAvailabilitySchedule(new Date(), accessPoint.timetable, timezone)
                      .map((schedule, i) => (
                        <div key={i} className={styles.schedule}>
                          {schedule}
                        </div>
                      ))}
                  </div>
              )}
            </div>
            <div className={styles.contentBottom}>
              <div>
                <div className={styles.spacer}></div>
                  {(vendorSchedule || vendorPassExpiresAtFormatted) &&
                    <div>Your Access Schedule:</div>
                  }
                  {vendorPassExpiresAtFormatted && !vendorSchedule &&
                    <div>{vendorPassActivatesAtFormatted} - {vendorPassExpiresAtFormatted}</div>
                  }
                  {vendorSchedule && <Timetable timetable={vendorSchedule} />}
              </div>
            </div>
            <Button
              className={styles.button}
              disabled={isUnlocking || !apScheduleAllowed || !vendorScheduleAllowed}
              expand
              type="primary"
              size="large"
              onClick={async () => {
                try {
                  await unlockAccessPointMutation({
                    variables: { accessPointId: accessPoint.accessPointId },
                  });

                  setLockState(LockState.UNLOCKED);
                  setIsUnlocking(true);
                  closeDrawer(accessPoint.accessTimeout);
                } catch (error) {
                  const castError = error as ApolloError | Error;
                  onUnlockError(castError);
                  setLockState(LockState.LOCKED);
                  setIsUnlocking(false);
                }
              }}
            >
              Unlock {accessPoint.name}
            </Button>
            <BottomDrawerModal
              className={isUnlocking ? styles.bottomDrawer : undefined}
              show={isUnlocking}
            >
              <Progress progress={completionPercent} />
              <div className={styles.content}>
                <div className={styles.iconContainer}>
                  <div className={styles.icon}>
                    {lockState === LockState.UNLOCKED || lockState === LockState.OPEN
                      ? <Icon.Unlock color={colors.black} />
                      : <Icon.Lock color={colors.black} />
                    }
                  </div>
                </div>
                <div className={styles.description}>
                  {`Locking in ${Math.round((timeLeft / 1000) % 60)} seconds...`}
                </div>
              </div>
            </BottomDrawerModal>
          </div>
          <Footer />
        </>
      )
    }

    return (
      <>
        <div className={styles.container}>
          <div>
            <Hero
              title={propertyName}
              subtitle={'Vendor Access'}
              cloudId={property.image?.cloudId}
              imageAlt={propertyName}
              status={heroStatus}
            />
            <div className={styles.header}>
              {header}
            </div>
            {subHeader && (
              <div className={styles.subHeader}>
                {
                  typeof subHeader === 'function'
                    ? subHeader(accessPoint.timetable, timezone)
                    : subHeader
                }
              </div>
            )}
          </div>
          <div className={styles.contentBottom}>
            <div className={styles.spacer}></div>
            <div>
              See a QR code? Open the QR Scanner below to unlock the entry point.
            </div>
            <div>
              <div className={styles.spacer}></div>
                {(vendorSchedule || vendorPassExpiresAtFormatted) &&
                  <div>Your Access Schedule:</div>
                }
                {vendorPassExpiresAtFormatted && !vendorSchedule &&
                  <div>{vendorPassActivatesAtFormatted} - {vendorPassExpiresAtFormatted}</div>
                }
                {vendorSchedule && <Timetable timetable={vendorSchedule} />}
            </div>
          </div>
          <Button
            className={styles.button}
            expand
            type="primary"
            size="large"
            onClick={() => setShowQRScanner(true)}
          >
            Scan Another QR Code
          </Button>
          {showQRScanner && (
            <QRScanner
              headerText={vendorPassExpiresAtFormatted}
              headerSubtext={vendorPassExpiresAtFormatted ? "Access Expires" : null}
              onClickBack={() => {
                setShowQRScanner(false);
              }}
            />
          )}
        </div>
        <Footer />
      </>
    );
  }

  return <LoadingSpinner />;
};

export default VendorAccess;
